import type { WorkspaceLanguage } from "./index";

export interface BilingualText {
  sourceLanguage: WorkspaceLanguage;
  original: string;
  de: string;
  en: string;
}

export type BilingualRecord<T> = {
  sourceLanguage: WorkspaceLanguage;
  original: T;
  de: T;
  en: T;
};

export class BilingualContentError extends Error {
  constructor(message = "Bilingual content could not be created.") {
    super(message);
    this.name = "BilingualContentError";
  }
}

export function isBilingualText(value: unknown): value is BilingualText {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<BilingualText>;
  return (candidate.sourceLanguage === "de" || candidate.sourceLanguage === "en") &&
    typeof candidate.original === "string" &&
    typeof candidate.de === "string" &&
    typeof candidate.en === "string";
}

export function readBilingualText(value: BilingualText | string | null | undefined, language: WorkspaceLanguage): string {
  if (!value) return "";
  if (isBilingualText(value)) {
    const resolved = value[language]?.trim();
    if (resolved) return resolved;
    return value.sourceLanguage === language ? value.original : "";
  }
  return language === "de" ? value : "";
}

export function hasBilingualTextLanguage(value: BilingualText | undefined, language: WorkspaceLanguage): boolean {
  return Boolean(value?.[language]?.trim());
}

export function hasBilingualRecordLanguage<T>(value: BilingualRecord<T> | undefined, language: WorkspaceLanguage): boolean {
  return Boolean(value?.[language]);
}

export function createBilingualText(value: string, sourceLanguage: WorkspaceLanguage, translated?: string): BilingualText {
  const original = value.trim();
  const targetLanguage: WorkspaceLanguage = sourceLanguage === "de" ? "en" : "de";
  const target = translated?.trim() ?? "";
  return {
    sourceLanguage,
    original,
    de: sourceLanguage === "de" ? original : targetLanguage === "de" ? target : "",
    en: sourceLanguage === "en" ? original : targetLanguage === "en" ? target : ""
  };
}

export function createBilingualRecord<T>(value: T, sourceLanguage: WorkspaceLanguage, translated: T): BilingualRecord<T> {
  const targetLanguage: WorkspaceLanguage = sourceLanguage === "de" ? "en" : "de";
  return {
    sourceLanguage,
    original: value,
    de: sourceLanguage === "de" ? value : targetLanguage === "de" ? translated : value,
    en: sourceLanguage === "en" ? value : targetLanguage === "en" ? translated : value
  };
}

function hasText(value: string): boolean {
  return value.trim().length > 0;
}

function hasTranslatedRecordText(source: Record<string, string>, translated: Record<string, string>): boolean {
  return Object.entries(source).every(([key, entry]) => {
    if (!hasText(entry)) return typeof translated[key] === "string";
    return typeof translated[key] === "string" && hasText(translated[key]);
  });
}

export async function createBilingualContent<T extends string | Record<string, string>>(
  value: T,
  sourceLanguage: WorkspaceLanguage,
  translate: (value: T, sourceLanguage: WorkspaceLanguage, targetLanguage: WorkspaceLanguage) => Promise<T>
): Promise<T extends string ? BilingualText : BilingualRecord<Extract<T, Record<string, string>>>> {
  const targetLanguage: WorkspaceLanguage = sourceLanguage === "de" ? "en" : "de";
  const translated = await translate(value, sourceLanguage, targetLanguage);

  if (typeof value === "string") {
    if (!hasText(value) || typeof translated !== "string" || !hasText(translated)) {
      throw new BilingualContentError();
    }
    return createBilingualText(value, sourceLanguage, translated) as unknown as T extends string ? BilingualText : BilingualRecord<Extract<T, Record<string, string>>>;
  }

  const sourceRecord = value as Extract<T, Record<string, string>>;
  const translatedRecord = translated as Extract<T, Record<string, string>>;
  if (
    !translatedRecord ||
    typeof translatedRecord !== "object" ||
    Array.isArray(translatedRecord) ||
    !hasTranslatedRecordText(sourceRecord, translatedRecord)
  ) {
    throw new BilingualContentError();
  }

  return createBilingualRecord(sourceRecord, sourceLanguage, translatedRecord) as unknown as T extends string ? BilingualText : BilingualRecord<Extract<T, Record<string, string>>>;
}

export function parseBilingualTextJson(value?: string | null): BilingualText | undefined {
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value) as unknown;
    return isBilingualText(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}
